import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/seo-packages-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/seo-packages-colorado")({
  head: () => ({
    meta: [
      { title: "SEO Packages & Monthly SEO Services: What Should Be Included (2026)" },
      { name: "description", content: "Comparing SEO packages? What monthly SEO services should actually include, honest Colorado pricing ($950–$2,750/mo), and the red flags to avoid." },
      { property: "og:title", content: "SEO Packages & Monthly SEO Services: What Should Be Included" },
      { property: "og:description", content: "What monthly SEO services should actually include, honest Colorado pricing, and the red flags to avoid." },
      { property: "og:url", content: `${SITE_URL}/learn/seo-packages-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "SEO packages explained",
        description: ARTICLE?.desc ?? "What monthly SEO services should include and what they cost.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/seo-packages-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="SEO"
      read="9 min read"
      title="SEO packages and monthly SEO services: what should actually be included"
      intro="Most SEO packages are a list of vague deliverables — 'optimization,' 'reporting,' 'content' — with no way to tell what you're buying. Here's what monthly SEO services should genuinely include, what they cost in Colorado, and how to spot a package that's mostly invoice."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "SEO packages" },
      ]}
      path="/learn/seo-packages-colorado"
      faqs={[
        { q: "How much should monthly SEO services cost?", a: "In Colorado, real monthly SEO for a local business runs $950 to $2,750 per month depending on how many services and cities you're targeting. Anything under $500/month is almost never enough work to move rankings — it's usually automated reports and a logo on someone else's spreadsheet." },
        { q: "Should an SEO package have a contract?", a: "Month-to-month is the standard you should demand. Long contracts exist to protect agencies from their own results. If the work is good, you stay because it's working — not because a contract forces you to." },
        { q: "What's the difference between SEO packages and hourly SEO?", a: "Packages bundle the ongoing work — content, maps, technical fixes — into a flat monthly rate. Hourly makes sense for one-off repairs (ours is $250/hour) but not for rankings, which need consistent monthly effort." },
      ]}
    >
      <h2>What a real monthly SEO package includes</h2>
      <p>
        Strip away the buzzwords and local SEO is four recurring jobs. A legitimate package does
        all of them, every month:
      </p>
      <ul>
        <li><strong>Google Business Profile work.</strong> Posts, photos, services, Q&amp;A, and review responses. This is what moves you in the Map Pack, where most local clicks happen.</li>
        <li><strong>Content that targets real searches.</strong> Pages and articles built around what your customers actually type — service pages, city pages, and answers to real questions.</li>
        <li><strong>Technical upkeep.</strong> Speed, mobile, schema markup, broken links, indexing. Small things that quietly decide whether Google trusts your site.</li>
        <li><strong>Plain-English reporting.</strong> What was done, what moved, what's next. If you can't understand the report, that's on purpose.</li>
      </ul>

      <h2>Honest Colorado pricing</h2>
      <p>
        Our published packages are a fair benchmark for the market:{" "}
        <strong>$950/month</strong> covers local SEO and Google Maps for one service area,{" "}
        <strong>$1,650/month</strong> adds AI/GEO optimization and social media, and{" "}
        <strong>$2,750/month</strong> covers multi-city SEO, Maps, AI/GEO, social, PPC
        management, and reputation management. Ad spend is separate. Compare the full breakdown
        on our <Link to="/pricing">pricing page</Link>.
      </p>

      <h2>Red flags in an SEO package</h2>
      <ul>
        <li><strong>Guaranteed rankings.</strong> Nobody controls Google. Guarantees mean the salesperson, not the work, is doing the talking.</li>
        <li><strong>6–12 month lock-ins.</strong> The agency gets paid whether or not anything improves. See our guide on <Link to="/learn/escape-marketing-agency-contracts-colorado">escaping agency contracts</Link>.</li>
        <li><strong>&ldquo;Proprietary&rdquo; anything.</strong> Your website, content, and Google profile should be yours. If leaving means losing everything, you were renting, not buying.</li>
        <li><strong>Reports full of impressions, empty of calls.</strong> Rankings and traffic are means. The point is your phone ringing.</li>
      </ul>

      <h2>How to compare two proposals</h2>
      <p>
        Ask both agencies the same three questions: What exactly will you do in month one? What
        will I own if I cancel? Can I talk to the person doing the work? The answers separate
        real packages from polished invoices faster than any feature list. Our{" "}
        <Link to="/learn/agency-questions">questions to ask a marketing agency</Link> has the
        full list.
      </p>

      <h2>What to do next</h2>
      <p>
        Want to know where you stand before spending anything? Request a{" "}
        <Link to="/free-audit">free digital marketing audit</Link> — we'll show you what's
        working, what's not, and which package (if any) fits. Or see how we handle{" "}
        <Link to="/colorado-seo">SEO across Colorado</Link>.
      </p>
    </ArticleLayout>
  );
}
